import React, { useMemo } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid";
import { IoDocumentTextOutline } from "react-icons/io5";
import { parseISO } from "date-fns";
import TrialCard from "./TrialCard";
import GlassCard from "components/GlassCard";
import { GRADIENTS } from "utils/designTokens";

const MAX_CARDS = 3;

// ---------------------------------------------------------------------------
// Single card skeleton shown while loading
// ---------------------------------------------------------------------------
const TrialCardSkeleton = () => (
  <GlassCard sx={{ height: "100%" }}>
    <Box p={2.5} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Skeleton animation="wave" width="40%" height={14} sx={{ bgcolor: "rgba(255,255,255,0.07)" }} />
      <Skeleton animation="wave" width="90%" height={16} sx={{ bgcolor: "rgba(255,255,255,0.07)" }} />
      <Skeleton animation="wave" width="75%" height={16} sx={{ bgcolor: "rgba(255,255,255,0.07)" }} />
      <Box mt={1}>
        <Skeleton animation="wave" width="30%" height={10} sx={{ bgcolor: "rgba(255,255,255,0.05)", mb: 0.5 }} />
        <Skeleton animation="wave" width="100%" height={13} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
        <Skeleton animation="wave" width="85%" height={13} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
      </Box>
      <Box>
        <Skeleton animation="wave" width="30%" height={10} sx={{ bgcolor: "rgba(255,255,255,0.05)", mb: 0.5 }} />
        <Skeleton animation="wave" width="100%" height={13} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
        <Skeleton animation="wave" width="70%" height={13} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
      </Box>
      <Box>
        <Skeleton animation="wave" width="30%" height={10} sx={{ bgcolor: "rgba(255,255,255,0.05)", mb: 0.5 }} />
        <Skeleton animation="wave" width="100%" height={13} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
      </Box>
    </Box>
  </GlassCard>
);

// ---------------------------------------------------------------------------
// Single card empty state (no data)
// ---------------------------------------------------------------------------
const TrialCardEmpty = () => (
  <GlassCard sx={{ height: "100%" }}>
    <Box
      p={2.5}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 220,
        gap: 1,
      }}
    >
      <Typography
        sx={{
          color: "rgba(255,255,255,0.25)",
          fontSize: "0.8rem",
          fontWeight: 500,
          textAlign: "center",
        }}
      >
        No registered clinical trials
      </Typography>
    </Box>
  </GlassCard>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const TrialIntelligenceCards = ({ trials, loading = false }) => {
  const latestTrials = useMemo(() => {
    if (!trials || trials.length === 0) return [];
    return [...trials]
      .sort((a, b) => {
        const dateA = a.last_updated ? parseISO(a.last_updated).getTime() : 0;
        const dateB = b.last_updated ? parseISO(b.last_updated).getTime() : 0;
        return dateB - dateA;
      })
      .slice(0, MAX_CARDS);
  }, [trials]);

  return (
    <Box>
      {/* Section header */}
      <Box display="flex" alignItems="center" mb={0.5}>
        <IoDocumentTextOutline size={20} color="#A78BFA" />
        <Typography
          variant="h6"
          sx={{
            background: GRADIENTS.chartAccent,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: 700,
            fontSize: "1.2rem",
            ml: 1,
          }}
        >
          Trial Intelligence Cards
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.8rem", mb: 2.5 }}
      >
        Detailed battle cards for the latest clinical studies
      </Typography>

      <Grid container spacing={3}>
        {loading ? (
          // Skeleton cards while fetching
          Array.from({ length: MAX_CARDS }).map((_, i) => (
            <Grid item xs={12} md={6} lg={4} key={`skeleton-${i}`}>
              <TrialCardSkeleton />
            </Grid>
          ))
        ) : latestTrials.length === 0 ? (
          // Empty-state cards — same frame, message inside
          Array.from({ length: MAX_CARDS }).map((_, i) => (
            <Grid item xs={12} md={6} lg={4} key={`empty-${i}`}>
              <TrialCardEmpty />
            </Grid>
          ))
        ) : (
          latestTrials.map((trial) => (
            <Grid item xs={12} md={6} lg={4} key={trial.nct_id}>
              <TrialCard trial={trial} />
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
};

export default TrialIntelligenceCards;
